import BusinessIcon from "@mui/icons-material/Business";
import SearchIcon from "@mui/icons-material/Search";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import Loader from "_shared/_views/loading/loader";
import { CollectionToolbar, CollectionToolbarSearch } from "_shared/_views/tables/table-toolbar";
import DebouncedTextField from "_shared/_views/text-input/debounced-text-field";
import Guid from "_shared/guids/guid";
import {
  isUnauthorizedVendorRolloutBoardError,
  useGetVendorRolloutBoardQuery,
} from "legal-entities/vendor-rollouts/_api/vendor-rollout-board-api-service";
import {
  VENDOR_ROLLOUT_BOARD_MAX_PAGE_SIZE,
  VendorRolloutBoardOrderField,
} from "legal-entities/vendor-rollouts/_api/vendor-rollout-board-query";
import {
  assembledAtLabel,
  ROLLOUT_STATUS_FILTER_STAGES,
  rolloutStageLabel,
} from "legal-entities/vendor-rollouts/_views/vendor-rollout-board-presentation";
import VendorRolloutStatusTable, {
  VendorRolloutBoardSort,
} from "legal-entities/vendor-rollouts/_views/vendor-rollout-status-table";
import { ReactNode, useState } from "react";

const BoardContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  width: "100%",
});
const BoardToolbar = styled(CollectionToolbar)(({ theme }) => ({
  paddingBottom: theme.spacing(1),
}));
const StatusFilter = styled(TextField)({
  minWidth: 240,
});
/** Holds its height while a page is in flight, so the table does not jump down when it finishes. */
const ProgressSlot = styled("div")({
  height: 4,
});
const BoardFooter = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  justifyContent: "space-between",
}));
const AssembledAt = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  paddingTop: theme.spacing(1),
}));
/**
 * What the board shows in place of the table when there are no roll-outs.
 *
 * The app's shared answer for an empty table --
 * [`_shared/_views/tables/table-empty-state.tsx`](../../../_shared/_views/tables/table-empty-state.tsx),
 * described in [table empty states](../../../../../docs/software-design/table-empty-states.md) --
 * draws itself inside the cell Material React Table leaves where the rows would be, and reads the
 * search and filter state off the table instance it is handed. This board is a hand-built MUI table
 * with grouped stage headers rather than an MRT one, and it replaces the table outright rather than
 * filling it, so there is no instance to give that component; the board says which empty it is.
 */
const EmptyBoard = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  padding: theme.spacing(4, 2),
  textAlign: "center",
}));
const EmptyBoardIcon = styled("div")(({ theme }) => ({
  color: theme.palette.text.disabled,
  display: "flex",
  "& > .MuiSvgIcon-root": { fontSize: "2.5rem" },
}));
const EmptyBoardDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

/** How many roll-outs one page of the board shows until the reader says otherwise. */
export const DEFAULT_PAGE_SIZE = 25;

/** The page sizes on offer. The top is the API's own maximum, past which it answers 400. */
export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, VENDOR_ROLLOUT_BOARD_MAX_PAGE_SIZE];

/** The longest search the API takes. The field stops there rather than sending one it refuses. */
export const SEARCH_MAX_LENGTH = 200;

/** What the search box is named, and therefore what a screen reader calls it. */
export const SEARCH_LABEL = "Search firm or team leader";

/** The status filter's "no filter" choice. */
export const ALL_STATUSES = "";

/** Firm name, A to Z — the order a reader looks a firm up in, and the server's own default. */
export const DEFAULT_SORT: VendorRolloutBoardSort = {
  orderBy: VendorRolloutBoardOrderField.FirmName,
  orderDir: OrderDirection.Ascending,
};

/**
 * The order after a header is pressed: the other direction on the column already in use, and
 * ascending on a new one.
 *
 * Ascending is the useful first answer for every column here — firms A to Z, the roll-outs that
 * have got least far first, and the longest-quiet first. The board exists to find the roll-outs
 * that have stalled, and each of those puts them at the top.
 */
export function nextSort(current: VendorRolloutBoardSort, field: VendorRolloutBoardOrderField): VendorRolloutBoardSort {
  if (current.orderBy !== field) return { orderBy: field, orderDir: OrderDirection.Ascending };
  return {
    orderBy: field,
    orderDir: current.orderDir === OrderDirection.Ascending ? OrderDirection.Descending : OrderDirection.Ascending,
  };
}

export type VendorRolloutStatusBoardProps = {
  /**
   * The client to report on. Omitted asks for every client at once, which is the staff question and
   * needs the platform grant — never a shorthand for the caller's own organization, which the API
   * refuses to infer. A client-facing surface always passes its own id.
   */
  readonly clientLegalEntityId?: Guid;
  /** What the empty state says. A client and a staff member are told different things. */
  readonly emptyDescription: string;
  readonly emptyTitle: string;
};

/**
 * The status board itself: one query, one table, and the states around them.
 *
 * Both surfaces the roll-out has are this component with a different scope, because the staff tab
 * and the client's page are the same question asked by two kinds of caller. The API says the same
 * of its single endpoint, and a second component here would be a second place for the answer to
 * drift.
 *
 * **Everything that narrows, orders or pages it is the server's work** (#5449). A client rolling the
 * platform out to many firms has more of them than one page holds, so the search, the status, the
 * column and the page all travel to `legal-entities/vendor-rollouts`. Any of the first three changing
 * is a new result set, and the reader goes back to its first page rather than to page four of
 * something whose page one they never saw.
 */
export default function VendorRolloutStatusBoard(props: Readonly<VendorRolloutStatusBoardProps>) {
  const clientLegalEntityId = props.clientLegalEntityId;
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(ALL_STATUSES);
  const [sort, setSort] = useState<VendorRolloutBoardSort>(DEFAULT_SORT);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const boardQuery = useGetVendorRolloutBoardQuery({
    clientLegalEntityId,
    search,
    status: status === ALL_STATUSES ? undefined : status,
    orderBy: sort.orderBy,
    orderDir: sort.orderDir,
    pageIndex,
    pageSize,
  });

  if (boardQuery.isLoading) return <Loader text="Loading firm onboarding status..." />;

  const board = boardQuery.data;
  const rows = board?.rows ?? [];
  const narrowed = search.trim().length > 0 || status !== ALL_STATUSES;

  // Nothing to narrow and nothing to page through: the board has no roll-outs at all, which is a
  // different sentence from "nothing matched" and has no use for a search box.
  if (!boardQuery.error && !boardQuery.isFetching && !narrowed && pageIndex === 0 && rows.length === 0) {
    return (
      <EmptyBoard role="status">
        <EmptyBoardIcon>
          <BusinessIcon />
        </EmptyBoardIcon>
        <Typography component="h2" variant="h6">
          {props.emptyTitle}
        </Typography>
        <EmptyBoardDescription variant="body2">{props.emptyDescription}</EmptyBoardDescription>
      </EmptyBoard>
    );
  }

  const clearNarrowing = () => {
    setSearch("");
    setStatus(ALL_STATUSES);
    setPageIndex(0);
  };

  let body: ReactNode;
  if (boardQuery.error) {
    const refused = isUnauthorizedVendorRolloutBoardError(boardQuery.error);
    body = (
      <Alert
        severity={refused ? "warning" : "error"}
        action={
          refused ? undefined : (
            <Button color="inherit" size="small" onClick={() => void boardQuery.refetch()}>
              Try again
            </Button>
          )
        }
      >
        {refused
          ? "You do not have permission to see these roll-outs."
          : "We could not load the firm onboarding status."}
      </Alert>
    );
  } else if (rows.length === 0) {
    body = (
      <EmptyBoard role="status">
        <Typography component="h2" variant="h6">
          No firms match
        </Typography>
        <EmptyBoardDescription variant="body2">
          {narrowed ? "Nothing on the board matches the search and status chosen." : "This page is past the end of the board."}
        </EmptyBoardDescription>
        <Button size="small" onClick={clearNarrowing}>
          {narrowed ? "Clear search and status" : "Back to the first page"}
        </Button>
      </EmptyBoard>
    );
  } else {
    body = (
      <TableContainer aria-busy={boardQuery.isFetching}>
        <VendorRolloutStatusTable
          rows={rows}
          showClient={clientLegalEntityId === undefined}
          sort={sort}
          onSortChange={(field) => {
            setSort(nextSort(sort, field));
            setPageIndex(0);
          }}
        />
      </TableContainer>
    );
  }

  return (
    <BoardContainer>
      <BoardToolbar>
        <CollectionToolbarSearch>
          <DebouncedTextField
            label={SEARCH_LABEL}
            size="small"
            value={search}
            onDebouncedChange={(value) => {
              setSearch(value);
              setPageIndex(0);
            }}
            slotProps={{
              htmlInput: { maxLength: SEARCH_MAX_LENGTH },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: 280 }}
          />
        </CollectionToolbarSearch>
        <StatusFilter
          select
          label="Status"
          size="small"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPageIndex(0);
          }}
        >
          <MenuItem value={ALL_STATUSES}>All statuses</MenuItem>
          {ROLLOUT_STATUS_FILTER_STAGES.map((stage) => (
            <MenuItem key={stage} value={stage}>
              {rolloutStageLabel(stage)}
            </MenuItem>
          ))}
        </StatusFilter>
      </BoardToolbar>
      <ProgressSlot>{boardQuery.isFetching && <LinearProgress aria-label="Loading roll-outs" />}</ProgressSlot>
      {body}
      <BoardFooter>
        {/* A stage that has happened but whose outbox row has not drained yet reports as the stage
            before it, so the board says when it was read rather than implying it is instantaneous. */}
        <AssembledAt variant="caption">{board ? assembledAtLabel(board.assembledAt) : null}</AssembledAt>
        <TablePagination
          component="div"
          count={board?.pagination.totalElements ?? 0}
          page={pageIndex}
          rowsPerPage={pageSize}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          showFirstButton
          showLastButton
          onPageChange={(_event, page) => setPageIndex(page)}
          onRowsPerPageChange={(event) => {
            // A different page size is a different set of pages, so the reader goes back to the
            // first rather than to whatever row number their old page index now points at.
            setPageSize(Number.parseInt(event.target.value, 10));
            setPageIndex(0);
          }}
        />
      </BoardFooter>
    </BoardContainer>
  );
}
